import React, { useEffect, useRef } from 'react';
import { Box, IconButton, Modal, Theme, Typography } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { SxProps } from '@mui/system/styleFunctionSx';

const style: SxProps<Theme> = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: {
    xl: '70%',
    lg: '70%',
    md: '80%',
    sm: '90%',
    xs: 'calc(100% - 16px)',
  },
  maxWidth: '400px',
  bgcolor: 'background.paper',
  //border: '2px solid #000',
  //boxShadow: 24,
  borderRadius: 1,
  maxHeight: {
    xs: 'calc(100dvh - 16px)',
    sm: 'calc(100dvh - 32px)',
  },
  overflow: 'hidden',
  display: 'flex',
  // Centering along the scroll axis makes overflowing content unreachable at
  // the top, so the column keeps its content anchored to the start.
  justifyContent: 'flex-start',
  alignItems: 'center',
  flexDirection: 'column',
};

interface IProps {
  open: boolean;
  handleClose: () => void;
  children?: React.ReactNode;
  title?: string;
  containerSx?: SxProps<Theme> | undefined;
  mtHeader?: number;
  closeDisabled?: boolean;
  disableBackdropClose?: boolean;
  stickyFooter?: boolean;
}

const ModalContainer = ({
  open,
  handleClose,
  children,
  title,
  containerSx = {},
  mtHeader = 4,
  closeDisabled = false,
  disableBackdropClose = false,
  stickyFooter = false,
}: IProps) => {
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && contentRef.current) contentRef.current.scrollTop = 0;
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={(_event, reason) => {
        if (closeDisabled) return;
        if (disableBackdropClose && reason === 'backdropClick') return;
        handleClose();
      }}
    >
      <Box sx={{ ...style, ...containerSx }}>
        <Box
          display="flex"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          px={2}
          pt={mtHeader}
          pb={2}
          flex="0 0 auto"
          bgcolor="background.paper"
          borderBottom={1}
          borderColor="divider"
          zIndex={2}
        >
          <Typography variant="h6">{title}</Typography>
          <IconButton
            aria-label="Chiudi finestra"
            onClick={handleClose}
            disabled={closeDisabled}
          >
            <ClearIcon color="primary" fontSize="large" />
          </IconButton>
        </Box>
        <Box
          ref={contentRef}
          data-testid="modal-scroll-content"
          width="100%"
          minHeight={0}
          overflow="auto"
          display="flex"
          flexDirection="column"
          justifyContent="flex-start"
          alignItems="center"
          px={2}
          pb={2}
          sx={
            stickyFooter
              ? {
                  '& [data-modal-footer]': {
                    position: 'sticky',
                    bottom: 0,
                    zIndex: 1,
                    bgcolor: 'background.paper',
                    borderTop: 1,
                    borderColor: 'divider',
                    py: 2,
                    mt: 2,
                  },
                }
              : undefined
          }
        >
          {children}
        </Box>
      </Box>
    </Modal>
  );
};

export default ModalContainer;
